// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2025-10-27 19:21:06 +0100 (Mon, 27 Oct 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 157537 $
//  Purpose   : gndo components
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Search, Icon, Button, Header, CardHeader, Card, CardContent} from 'semantic-ui-react'

// ---Framework imports
import {Sel, Actions} from 'uix/registries'
import {XPopup} from 'uix/components'
import {useApi, useState, useActions, useEffect, useSelector, useRef} from 'uix/hooks'
import {formatDate} from 'uix/utils'
import {XMenu, Toolbar} from 'uix/components'
import log from 'uix/logging'

// ---Globals

export const GND_TYPES = Object.freeze({name: 1, subject: 2, place: 3})
export const GND_TYPE_META = {
  [GND_TYPES.name]: {
    entityPrefix: 'name',
    icon: 'user circle',
  },
  [GND_TYPES.subject]: {
    entityPrefix: 'subject',
    icon: 'book',
  },
  [GND_TYPES.place]: {
    entityPrefix: 'place',
    icon: 'globe',
  },
}
export const gndTypeSimplify = typelist => {
  let cases = {
    Person: GND_TYPES.name,
    Family: GND_TYPES.name,
    PlaceOrGeographicName: GND_TYPES.place,
    CorporateBody: GND_TYPES.name,
    SubjectHeading: GND_TYPES.subject,
    Work: GND_TYPES.subject,
    ConferenceOrEvent: GND_TYPES.name,
  }
  for (let [key, value] of Object.entries(cases)) {
    if (typelist.includes(key)) {
      return value
    }
  }
  return undefined
}

const formatPersonDate = d => {
  // for dateOfBirth or dateOfDeath
  if (d.startsWith('-') || !d.includes('-')) {
    // year granularity
    return String(parseInt(d))
  } else {
    return formatDate(d)
  }
}

export const GndLink = ({gndid, content}) => {
  /* A link to an external website for the GND-ID*/
  return (
    <a style={{paddingTop: '0.5rem'}} target="_gnd" href={`https://d-nb.info/gnd/${gndid}`}>
      {content || gndid}
    </a>
  )
}

export const GndPopupInfo = ({data}) => {
  /* A small info box with basic data for a GND-ID, e.g. to be us ed in a popup. */
  let i18n = useSelector(Sel.i18n)
  let lang = useSelector(Sel.language)
  let content = null
  let header = null
  if (data?.gndo) {
    let {preferredName, biographicalOrHistoricalInformation, dateOfBirth} = data.gndo
    let wiki = null
    Object.values(data?.gndo.sameAs).map(value => {
      value.id.includes(`${lang == 'de' || lang == 'en' ? lang : 'en'}.wikipedia`) &&
        (wiki = value.id)
    })
    if (preferredName) {
      header = (
        <>
          <Header style={{margin: 'auto 0'}} as="h3">
            {preferredName}
          </Header>
          <Toolbar size="default" attached={false} style={{marginTop: '0', width: 'inherit'}}>
            <XMenu.Item
              size="small"
              icon="dnb"
              title="dnb-Link"
              onClick={e => e.stopPropagation()}
              href={`${data.gndo.id}`}
              target="_blank"
            />
            {wiki && (
              <XMenu.Item
                style={{marginLeft: '0.5rem'}}
                icon="wikipedia"
                size="small"
                title="wikipedia-Link"
                onClick={e => e.stopPropagation()}
                href={`${wiki}`}
                target="_blank"
              />
            )}
          </Toolbar>
        </>
      )
      if (dateOfBirth && dateOfBirth.length) {
        content = (
          <>
            <CardContent>
              <span> ({formatPersonDate(dateOfBirth[0])})</span>
            </CardContent>
          </>
        )
      }
    }
    if (biographicalOrHistoricalInformation) {
      content = (
        <>
          <CardContent>
            <div>{biographicalOrHistoricalInformation[0]}</div>
          </CardContent>
        </>
      )
    }
  } else if (!data) {
    header = <CardHeader>{i18n.get('noEntriesAvailable')}</CardHeader>
  }
  return (
    <Card fluid style={{maxWidth: '250px'}}>
      <CardHeader
        style={{
          padding: '0.3rem 0.8rem',
          display: 'flex',
          justifyContent: 'space-between',
          verticalAlign: 'middle',
        }}
      >
        {header}
      </CardHeader>
      {content}
    </Card>
  )
}

const GndPopupData = ({gndid, otid}) => {
  let {data, loading} = useApi(
    'gndo/getLiveInfo',
    {gndid, otid},
    {cached: true, condition: gndid || otid}
  )
  if (loading) return null
  if (!data) return null
  return <GndPopupInfo data={data} />
}

export const GndPopup = ({gndid, otid, trigger}) => {
  return (
    <XPopup
      fitted
      basic
      wide
      hoverable
      position="left center"
      on="hover"
      mouseEnterDelay={500}
      trigger={trigger}
    >
      <GndPopupData gndid={gndid} otid={otid} />
    </XPopup>
  )
}

export const GndItemDetails = ({gndid}) => {
  return (
    <>
      <GndPopupInfo gndid={gndid} />
      <GndLink
        gndid={gndid}
        content={
          <>
            <Icon name="linkify" />
            {gndid}
          </>
        }
      />
    </>
  )
}

export const GndSelect = ({init, onResultSelect, selected_words}) => {
  let [callApi, errorNotification] = useActions([Actions.callApi, Actions.errorNotification])
  let [result_gndid, set_result_gndid] = useState(null)
  // XXX: change this "init" implementation
  useEffect(() => set_result_gndid(null), [init])

  let content = null
  if (result_gndid) {
    content = (
      <>
        <GndItemDetails gndid={result_gndid} />
        <Button
          icon="check"
          content="Übernehmen"
          title="Setzt die Metadaten auf diesen Eintrag"
          onClick={() => {
            // TODO: can result_gndid be changed inbetween? (-> incorrect)
            callApi(
              'gndo/getLiveInfo',
              {gndid: result_gndid},
              (data, error) => {
                if (!error) {
                  let simplified_type = gndTypeSimplify(data.gndo.type)
                  if (simplified_type !== undefined) {
                    onResultSelect(simplified_type, data.gndid)
                  } else {
                    errorNotification({
                      header: 'Fehler beim Speichern.',
                      message: 'Typ wurde nicht verstanden.',
                    })
                  }
                }
              },
              {cached: true}
            )
          }}
        />
      </>
    )
  }
  return (
    <>
      <GndSearch
        init={init}
        onResultSelect={gnid => {
          set_result_gndid(gnid)
        }}
        selected_words={selected_words}
      />
      {content}
    </>
  )
}

export const GndSearch = ({init, onSearch, onResultSelect, selected_words}) => {
  /* An input widget with autocomplete functionality to select a GND ID.
  Visually displays a text, while the corresponding value will be the gnd id.

  The user can enter both text or an ID for input/search.
  */
  let [loading, set_loading] = useState(false)
  let [search_value, _set_search_value] = useState('')
  let default_value = null // initial value
  let [suggestions, set_suggestions] = useState([])
  let [callApi] = useActions([Actions.callApi])
  let searchRef = useRef()

  // XXX: review this "init" implementation (do a editCancel->startEdit cycle?, make GndSearch/GndSelect be controlled?)
  useEffect(() => set_search_value(''), [init])

  let set_search_value = query => {
    if (query.length > 2) {
      update_suggestions(query)
    } else {
      set_suggestions([])
    }
    _set_search_value(query)
  }

  let update_suggestions = query => {
    // TODO: debounce this?
    set_loading(true)
    callApi(
      'searchGndo',
      {query, suggest: true, size: 30},
      ({result}, error) => {
        if (!error) {
          set_suggestions(
            result.map(r => ({
              title: r.label,
              id: r.id.match(/([^\/]+)$/)[1],
              description: r.category,
            }))
          )
        }
        set_loading(false)
        searchRef.current.open()
      },
      {cached: true}
    )
  }
  return (
    <Search
      fluid
      //icon={null}
      loading={loading}
      //defaultValue={default_value}
      minCharacters={2} // minimum number of characters for query
      noResultsMessage="Keine GND IDs gefunden."
      onResultSelect={(e, {result}) => {
        onResultSelect(result.id)
      }}
      onSearchChange={(e, {value}) => {
        set_search_value(value)
      }}
      onClick={e => {
        if (!search_value) {
          set_search_value(selected_words.join(''))
        }
      }}
      resultRenderer={result => (
        <>
          <div
            style={{
              lineHeight: '1.5em',
              maxHeight: '1.5em',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
            title={result.title}
          >
            {result.title}
          </div>
          <div
            // TODO: grab color (and font-size) from a style-sheet?
            style={{
              fontSize: '0.8em',
              lineHeight: '1.2em',
              maxHeight: '1.2em',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              color: 'gray',
            }}
            title={result.description}
          >
            {result.description}
          </div>
        </>
      )}
      results={suggestions}
      value={search_value}
      ref={searchRef}
    />
  )
}
